// Metronome Response Task (MRT) engine.
//
// MRT does not fit the per-trial phase chain the other tasks use: it is a
// continuous stream of beats at a fixed rhythm, and the participant taps along.
// jsPsych's per-trial setTimeout chaining would accumulate timing drift across
// 60+ beats, which is fatal for a rhythm task. So MRT runs on its own engine
// here, with beat times computed as absolute multiples of the cycle period (the
// drift-free property the desktop beat scheduler provides) rather than chained.
//
// Each beat is a cycle: pre-beat gap -> beat (flash and/or tone) -> post-beat
// gap. A tap anywhere in a cycle is that beat's response. The recorded RT
// mirrors the desktop exactly: a tap before the beat is measured from the cycle
// start, a tap at or after the beat from the beat onset.

import {
  flattenTask,
  computeTrialResult,
  hasResponsePhase,
  joliveKeyToJsPsych,
  type TaskDefinition,
  type ResultTrial,
} from "./translator";
// Single source of truth for style-value validation (used lazily inside
// startBlock, so the TaskRenderer<->mrt import cycle is benign).
import { safeColor, safeNum } from "../TaskRenderer";

interface MrtResult {
  task_name: string;
  task_type: string;
  key_assignment: string | null;
  trials: ResultTrial[];
  end_trigger: "complete" | "cancelled";
}

interface Cfg {
  preBeat: number;
  stim: number;
  post: number;
  mode: string; // visual | auditory | both
  flashColor: string;
  fontSize: number;
  useDefaultTone: boolean;
  tapKey: string; // jsPsych/browser key (e.g. " ")
  tapName: string; // JOLIVE key name (e.g. "space")
}

function instructionHtml(text: string): string {
  const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return (
    `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center">` +
    `<div style="font-family:Arial,sans-serif;max-width:900px;color:#FFFFFF;font-size:26px;line-height:1.5;white-space:pre-line;text-align:center">` +
    `${esc}<div style="margin-top:40px;color:#AAAAAA;font-size:16px">Press SPACE to continue</div></div></div>`
  );
}

function scheduleTone(ctx: AudioContext, atTime: number): void {
  // 800 Hz sine, ~80ms, matching the desktop default metronome tone.
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = 800;
  const t = Math.max(atTime, ctx.currentTime);
  gain.gain.setValueAtTime(0.8, t);
  gain.gain.setValueAtTime(0.8, t + 0.078);
  gain.gain.linearRampToValueAtTime(0, t + 0.085);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.09);
}

// Run all the beats of one block on a drift-free clock. Calls onDone with a map
// of beat index -> the captured tap {key, rt} (absent beats had no tap).
function runBlock(
  dotEl: HTMLElement,
  beatStimuli: { text?: string | undefined; play_sound?: boolean | undefined }[],
  cfg: Cfg,
  audioCtx: AudioContext | null,
  onDone: (capturedByIndex: Record<number, { key: string; rt: number }>) => void,
): () => void {
  const n = beatStimuli.length;
  const period = cfg.preBeat + cfg.stim + cfg.post;
  const blockStart = performance.now();
  const taps: number[] = [];

  const onKey = (e: KeyboardEvent) => {
    if (e.key === cfg.tapKey && !e.repeat) taps.push(performance.now() - blockStart);
  };
  window.addEventListener("keydown", onKey);

  // Schedule tones up front against the sample-accurate audio clock.
  if (audioCtx && (cfg.mode === "auditory" || cfg.mode === "both")) {
    const base = audioCtx.currentTime + 0.06;
    for (let i = 0; i < n; i++) {
      if (beatStimuli[i]?.play_sound) {
        scheduleTone(audioCtx, base + (i * period + cfg.preBeat) / 1000);
      }
    }
  }

  let rafId = 0;
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    cancelAnimationFrame(rafId);
    window.removeEventListener("keydown", onKey);
    dotEl.textContent = "";

    const captured: Record<number, { key: string; rt: number }> = {};
    for (const tap of taps) {
      const i = Math.floor(tap / period);
      if (i < 0 || i >= n) continue;
      if (captured[i] !== undefined) continue; // first tap in the cycle wins
      const cycleStart = i * period;
      const beatOnset = cycleStart + cfg.preBeat;
      const rt = tap < beatOnset ? tap - cycleStart : tap - beatOnset;
      captured[i] = { key: cfg.tapName, rt };
    }
    onDone(captured);
  };

  const showVisual = cfg.mode === "visual" || cfg.mode === "both";
  const loop = () => {
    const t = performance.now() - blockStart;
    if (t >= n * period) {
      finish();
      return;
    }
    const i = Math.floor(t / period);
    const within = t - i * period;
    const stim = beatStimuli[i];
    const flashing =
      showVisual && !!stim?.text && within >= cfg.preBeat && within < cfg.preBeat + cfg.stim;
    dotEl.textContent = flashing ? String(stim!.text) : "";
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);

  return () => {
    finished = true;
    cancelAnimationFrame(rafId);
    window.removeEventListener("keydown", onKey);
  };
}

// Run the whole MRT task into `host`. Returns a cleanup function.
export function runMrt(
  host: HTMLElement,
  def: TaskDefinition,
  respKeys: Record<string, string>,
  onComplete: (result: MrtResult) => void,
  // P4: called at each BLOCK boundary with the cumulative NON-practice trial
  // count. MRT surveys snap to block boundaries (pausing mid-rhythm would break
  // the metronome), so the caller decides whether a survey is due and, if so,
  // shows it and resolves once answered before the next block starts.
  onBlockBoundary?: (nonPracticeTrials: number) => Promise<void> | void,
): () => void {
  const settings = def.settings ?? {};
  const tapName = Object.keys(respKeys)[0] ?? "space";
  const cfg: Cfg = {
    preBeat: (settings.pre_beat_ms as number) ?? 288,
    stim: (settings.stim_duration_ms as number) ?? 75,
    post: (settings.post_beat_ms as number) ?? 287,
    mode: (settings.metronome_mode as string) ?? "visual",
    flashColor: (settings.flash_color as string) ?? "#FFFFFF",
    fontSize: (settings.font_size as number) ?? 72,
    useDefaultTone: Boolean(settings.use_default_tone),
    tapKey: joliveKeyToJsPsych(tapName),
    tapName,
  };

  host.style.background = (settings.background_color as string) ?? "#000000";

  const blocks = flattenTask(def);
  const trialHasResp = hasResponsePhase(def);
  const allResults: ResultTrial[] = [];
  let audioCtx: AudioContext | null = null;
  let cancelled = false;
  let cleanupBlock: () => void = () => {};

  const ensureAudio = () => {
    if (cfg.mode === "auditory" || cfg.mode === "both") {
      if (!audioCtx) {
        try {
          audioCtx = new AudioContext();
        } catch {
          audioCtx = null;
        }
      }
      if (audioCtx && audioCtx.state === "suspended") void audioCtx.resume();
    }
  };

  const submit = () => {
    const keyAssignment =
      Object.entries(respKeys)
        .map(([k, v]) => `${k}:${v}`)
        .join(",") || null;
    onComplete({
      task_name: def.task_name ?? "",
      task_type: def.task_type ?? "mrt",
      key_assignment: keyAssignment,
      trials: allResults,
      end_trigger: "complete",
    });
  };

  let activeKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  const showInstruction = (text: string, onContinue: () => void) => {
    host.innerHTML = instructionHtml(text);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === " ") {
        window.removeEventListener("keydown", onKey);
        activeKeyHandler = null;
        ensureAudio(); // first gesture: unlock/resume audio
        onContinue();
      }
    };
    activeKeyHandler = onKey;
    window.addEventListener("keydown", onKey);
  };

  let bi = 0;
  let mrtNonPracticeTrials = 0;   // cumulative, for the "every N trials" boundary
  const startBlock = () => {
    if (cancelled) return;
    const block = blocks[bi];
    host.innerHTML =
      `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center">` +
      `<div id="jol-mrt-dot" style="font-family:Arial,sans-serif;color:${safeColor(cfg.flashColor, "#FFFFFF")};font-size:${safeNum(cfg.fontSize, 72)}px;font-weight:700;line-height:1"></div></div>`;
    const dotEl = host.querySelector("#jol-mrt-dot") as HTMLElement;
    const beatStimuli = block.trials.map((p) => ({
      text: p.stimulus.text as string | undefined,
      play_sound: p.stimulus.play_sound as boolean | undefined,
    }));
    cleanupBlock = runBlock(dotEl, beatStimuli, cfg, audioCtx, (capturedByIndex) => {
      if (cancelled) return;
      block.trials.forEach((plan, i) => {
        allResults.push(computeTrialResult(plan, capturedByIndex[i], respKeys, trialHasResp));
      });
      if ((block.label ?? "").trim().toLowerCase() !== "practice")
        mrtNonPracticeTrials += block.trials.length;
      bi += 1;
      // Offer a mid-block survey at this block boundary (snap-to-block), then
      // continue to the next block once it's answered.
      const cont = () => { if (!cancelled) nextBlock(); };
      if (onBlockBoundary) {
        Promise.resolve(onBlockBoundary(mrtNonPracticeTrials)).then(cont);
      } else {
        cont();
      }
    });
  };

  const nextBlock = () => {
    if (cancelled) return;
    if (bi >= blocks.length) {
      submit();
      return;
    }
    const block = blocks[bi];
    const isPractice = (block.label ?? "").trim().toLowerCase() === "practice";
    const prevWasPractice =
      bi > 0 && (blocks[bi - 1].label ?? "").trim().toLowerCase() === "practice";
    // Run this block: its authored instructions (if any), then the trials.
    const proceed = () =>
      block.instructions ? showInstruction(block.instructions, startBlock) : startBlock();
    // Practice -> test boundary: show the same gate the desktop task runner
    // shows automatically, so online participants get a break before the real
    // trials instead of flowing straight in (QA 2026-07-24, BUG-038).
    if (prevWasPractice && !isPractice) {
      showInstruction(
        "Practice complete.\n\nThe test will now begin.\n\n" +
          "When you are ready, press SPACE to start.",
        proceed,
      );
    } else {
      proceed();
    }
  };

  if (def.instructions) showInstruction(def.instructions, nextBlock);
  else {
    ensureAudio();
    nextBlock();
  }

  return () => {
    cancelled = true;
    if (activeKeyHandler) {
      window.removeEventListener("keydown", activeKeyHandler);
      activeKeyHandler = null;
    }
    cleanupBlock();
    if (audioCtx) void audioCtx.close();
  };
}
